import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import BoltIcon from '@mui/icons-material/Bolt';
import DevicesIcon from '@mui/icons-material/Devices';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Helmet } from 'react-helmet-async';
import { Logo } from '../components/Logo';
import { AppFooter } from '../components/AppFooter';
import { publicApi, type LandingCompetition } from '../api/resultsApi';
import { formatDateCz } from '../utils/dateFormat';

export const PublicLandingPage = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const { data: landing } = useQuery({
    queryKey: ['public', 'landing'],
    queryFn: publicApi.landing,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const live = landing?.liveCompetitions ?? [];

  return (
    <>
      <Helmet>
        <title>Hasičské soutěže — živé výsledky | HasičiOnline.cz</title>
        <meta
          name="description"
          content="Sledujte živé výsledky hasičských soutěží. Plamen, dorost, požární sport — naskenujte QR kód a sledujte bez registrace."
        />
      </Helmet>

      <Box sx={{ bgcolor: 'background.default', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Hero */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
            color: 'common.white',
            py: { xs: 5, md: 7 },
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, md: 7 }}>
                <Stack spacing={2.5}>
                  <Logo size={72} color="#FFFFFF" subtitleColor="rgba(255,255,255,0.9)" />
                  <Typography
                    variant="h2"
                    fontWeight={800}
                    sx={{ fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.1 }}
                  >
                    Živé výsledky hasičských soutěží
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.92, maxWidth: 560, fontWeight: 400 }}>
                    Sledujte výsledky živě, jak se zapisují. Bez registrace, bez instalace.
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                <Paper sx={{ p: { xs: 3, sm: 3.5 }, boxShadow: 10, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" fontWeight={700} color="text.primary">
                      Kód soutěže
                    </Typography>
                    <Box
                      component="form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (code.trim()) navigate(`/s/${code.trim().toUpperCase()}`);
                      }}
                    >
                      <Stack spacing={1.5}>
                        <TextField
                          placeholder="Např. ABC123"
                          value={code}
                          onChange={(e) => setCode(e.target.value.toUpperCase())}
                          fullWidth
                          autoFocus
                          slotProps={{
                            htmlInput: { maxLength: 10, style: { fontSize: '1.25rem', letterSpacing: 2 } },
                          }}
                        />
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={<VisibilityIcon />}
                          disabled={!code.trim()}
                          sx={{
                            py: 1.25,
                            bgcolor: '#1565C0',
                            '&:hover': { bgcolor: '#0D47A1' },
                          }}
                        >
                          Zobrazit výsledky
                        </Button>
                      </Stack>
                    </Box>
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      Kód najdete na QR kódu nebo plakátu v místě konání
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Probíhá nyní */}
        {live.length > 0 && (
          <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
              <Chip
                label="● LIVE"
                color="error"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  animation: 'landingPulse 2s infinite',
                  '@keyframes landingPulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.55 },
                  },
                }}
              />
              <Typography variant="h4" fontWeight={800}>
                Probíhá nyní
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              {live.map((c) => (
                <Grid key={c.shortCode} size={{ xs: 12, md: 6 }}>
                  <CompetitionCard c={c} />
                </Grid>
              ))}
            </Grid>
          </Container>
        )}

        {/* Kdyz zadna soutez nebezi — vyplnime prostor featurami misto prazdneho pruhu */}
        {live.length === 0 && (
          <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 }, flex: 1 }}>
            <Stack spacing={1} sx={{ mb: 4, textAlign: { xs: 'center', md: 'left' } }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }}
              >
                <SearchOffIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="overline" sx={{ letterSpacing: '0.15em', color: 'text.secondary' }}>
                  Právě žádná soutěž neprobíhá
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight={800}>
                Jak to funguje
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 720 }}>
                Žádný účet, žádná instalace — stačí kód ze soutěže. Až nějaká začne, objeví se nahoře.
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              {[
                {
                  icon: <QrCodeScannerIcon sx={{ fontSize: 32 }} />,
                  title: 'Naskenujte kód',
                  desc: 'QR kód najdete na plakátu nebo na startovní listině. Stačí fotoaparát v telefonu.',
                },
                {
                  icon: <BoltIcon sx={{ fontSize: 32 }} />,
                  title: 'Výsledky v reálném čase',
                  desc: 'Pořadatel zapíše výsledek a za pár sekund ho vidíte i vy.',
                },
                {
                  icon: <EmojiEventsIcon sx={{ fontSize: 32 }} />,
                  title: 'Disciplíny i celkové pořadí',
                  desc: 'Požární útok, štafety, běh přes překážky. Průběžné i finální pořadí kategorií.',
                },
                {
                  icon: <DevicesIcon sx={{ fontSize: 32 }} />,
                  title: 'Z mobilu, tabletu i počítače',
                  desc: 'Stejný odkaz funguje na telefonu, tabletu i počítači.',
                },
              ].map((feature) => (
                <Grid key={feature.title} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ color: 'primary.main' }}>{feature.icon}</Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.desc}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        )}

        <AppFooter />
      </Box>
    </>
  );
};

interface CompetitionCardProps {
  c: LandingCompetition;
}

const CompetitionCard = ({ c }: CompetitionCardProps) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      borderColor: 'error.main',
      borderWidth: 2,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
      <Chip
        label="● LIVE"
        color="error"
        size="small"
        sx={{
          fontWeight: 800,
          letterSpacing: '0.1em',
          animation: 'cardPulse 2s infinite',
          '@keyframes cardPulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.55 },
          },
        }}
      />
    </Box>
    <CardActionArea component={RouterLink} to={`/s/${c.shortCode}`} sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={1.5}>
          <Typography
            variant="overline"
            fontWeight={700}
            sx={{ letterSpacing: '0.1em', color: 'primary.main' }}
          >
            {c.unitName ?? '—'}
          </Typography>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, pr: 7 }}>
            {c.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDateCz(c.competitionDate)}
            {c.location ? ` • ${c.location}` : ''}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`${c.teamCount} ${teamsLabel(c.teamCount)}`} />
          </Stack>
          {c.winnerTeamName && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
              <EmojiEventsIcon sx={{ color: '#FFD54F', fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Vítěz: {c.winnerTeamName}
                {c.winnerCategoryName && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' '}
                    ({c.winnerCategoryName})
                  </Typography>
                )}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pt: 1, color: 'primary.main' }}>
            <Typography variant="body2" fontWeight={700}>
              Sledovat živě
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Stack>
        </Stack>
      </CardContent>
    </CardActionArea>
  </Card>
);

const teamsLabel = (n: number): string => {
  if (n === 1) return 'tým';
  if (n >= 2 && n <= 4) return 'týmy';
  return 'týmů';
};
